import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState, type FormEvent } from 'react';
import { Link, NavLink, useNavigate } from 'react-router';
import { errorMessage, useApi } from '../api';
import { AppFooter } from '../components/AppFooter';
import { Logo } from '../components/Logo';
import { Alert, Field, Loading } from '../components/ui';
import { useI18n } from '../i18n';
import type { Me, Membership, StaffRole } from '../session';

interface ConsoleData {
  roles: StaffRole[];
  tenants: { id: string; slug: string; name: string; legalStructure: Membership['legalStructure']; kind: 'client' | 'demo'; status: 'onboarding' | 'active'; units: number; createdAt: string; onboardingDone: number }[];
  supportAccesses: { id: string; tenantId: string; tenantName: string; reason: string; hours: number; status: string; requestedAt: string; decidedAt: string | null; expiresAt: string | null }[];
  team: { userId: string; email: string | null; name: string | null; role: StaffRole; grantedAt: string; lastSeenAt: string | null }[] | null;
}

/** Landing page of Syndiqia staff (docs/engineering/roles.md): demos, real clients (metadata only), support accesses, the team. */
export function ConsolePage({ me }: { me: Me }) {
  const { t } = useI18n();
  const c = t.console;
  const api = useApi();
  const data = useQuery({ queryKey: ['console'], queryFn: () => api<ConsoleData>('/api/console') });
  const supported = me.memberships.filter((m) => m.access === 'support');
  const own = me.memberships.filter((m) => m.access === 'member');

  return (
    <div className="chooser console">
      <ConsoleHeader me={me} />
      <h1>{c.title}</h1>
      <p className="muted">{c.intro(me.staff.map((r) => c.roles[r]).join(', '))}</p>

      {own.length > 0 && (
        <section>
          <h2>{c.own}</h2>
          <TenantCards items={own} />
        </section>
      )}
      {supported.length > 0 && (
        <section>
          <h2>{c.supportOpen}</h2>
          <TenantCards items={supported} badge={c.supportBadge} />
        </section>
      )}

      {data.isPending && <Loading label={t.loading} />}
      {data.isError && <Alert>{errorMessage(data.error, t)}</Alert>}
      {data.data && <Clients data={data.data} />}
      {data.data?.team && <Team team={data.data.team} />}
      <AppFooter />
    </div>
  );
}

/** Top of the staff pages: logo, the Console / Outils de vente tabs (sales tools for platform admins and sales), language. */
export function ConsoleHeader({ me }: { me: Me }) {
  const { t, locale, setLocale } = useI18n();
  const sales = me.staff.includes('platform_admin') || me.staff.includes('sales');
  return (
    <header className="chooser-header console-header">
      <Logo height={44} />
      {sales && (
        <nav className="console-tabs" aria-label={t.console.title}>
          <NavLink to="/" end>
            {t.console.title}
          </NavLink>
          <NavLink to="/ventes">{t.salesTools.title}</NavLink>
        </nav>
      )}
      <button type="button" className="link-button" onClick={() => setLocale(locale === 'fr' ? 'en' : 'fr')}>
        {t.language}
      </button>
    </header>
  );
}

export function TenantCards({ items, badge, resettable }: { items: Membership[]; badge?: string; resettable?: boolean }) {
  const { t, date } = useI18n();
  const api = useApi();
  const [resetting, setResetting] = useState<string[]>([]);
  const reset = useMutation({
    mutationFn: (tenantId: string) => api(`/api/console/demos/${tenantId}/reset`, { method: 'POST' }),
    onSuccess: (_r, tenantId) => setResetting((r) => [...r, tenantId]),
  });
  return (
    <ul className="chooser-grid">
      {items.map((m) => (
        <li key={m.tenantId}>
          <Link to={`/s/${m.slug}`} className={`chooser-card chooser-${m.legalStructure}`}>
            <span className="chooser-kind">
              {t.chooseKinds[m.legalStructure]}
              {badge && <span className="badge badge-ready"> {badge}</span>}
            </span>
            <strong className="chooser-name">{m.name}</strong>
            {m.expiresAt && <span className="muted small">{t.console.expires(date(m.expiresAt.slice(0, 10)))}</span>}
            <span className="chooser-open">{t.chooseOpen} →</span>
          </Link>
          {resettable && (
            <div className="demo-reset">
              {resetting.includes(m.tenantId) ? (
                <span className="muted small">{t.console.resetting}</span>
              ) : (
                <button type="button" className="link-button small" disabled={reset.isPending} onClick={() => window.confirm(t.console.confirmReset(m.name)) && reset.mutate(m.tenantId)}>
                  {t.console.reset}
                </button>
              )}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Starts onboarding a new client: created "en mise en service", then the phases run in the client's space. */
function StartOnboarding({ structure, onCancel }: { structure: Membership['legalStructure']; onCancel: () => void }) {
  const { t } = useI18n();
  const c = t.console;
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [touched, setTouched] = useState(false);
  const auto = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
  const start = useMutation({
    mutationFn: () => api<{ id: string; slug: string }>('/api/console/clients', { method: 'POST', body: { name, structure, slug: touched ? slug : auto } }),
    onSuccess: async (r) => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      await queryClient.invalidateQueries({ queryKey: ['console'] });
      navigate(`/s/${r.slug}/onboarding`);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim().length >= 3) start.mutate();
  };
  return (
    <form className="panel form console-start" onSubmit={submit}>
      <h3>{structure === 'npo_with_servitudes' ? c.onboardAssociation : c.onboardSyndicate}</h3>
      <div className="row">
        <Field label={c.newClientName} hint={c.newClientNameHint}>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label={c.newClientSlug} hint={c.newClientSlugHint}>
          <input value={touched ? slug : auto} onChange={(e) => (setTouched(true), setSlug(e.target.value.toLowerCase()))} />
        </Field>
      </div>
      {start.isError && <Alert>{errorMessage(start.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={start.isPending || name.trim().length < 3}>
          {c.startOnboarding}
        </button>
        <button type="button" className="button" onClick={onCancel}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}

/** Real clients: metadata only. Support staff ask for a temporary access; a director of the client decides. */
function Clients({ data }: { data: ConsoleData }) {
  const { t, date } = useI18n();
  const c = t.console;
  const clients = data.tenants.filter((x) => x.kind === 'client');
  const [asking, setAsking] = useState<string | null>(null);
  const [starting, setStarting] = useState<Membership['legalStructure'] | null>(null);
  const canOnboard = data.roles.includes('platform_admin') || data.roles.includes('sales');
  return (
    <section>
      <div className="console-clients-head">
        <h2>{c.clients}</h2>
        {canOnboard && (
          <div className="console-onboard-buttons">
            <button type="button" className="button button-small button-primary" onClick={() => setStarting(starting === 'npo_with_servitudes' ? null : 'npo_with_servitudes')}>
              + {c.onboardAssociation}
            </button>
            <button type="button" className="button button-small" onClick={() => setStarting(starting === 'divided_coownership' ? null : 'divided_coownership')}>
              + {c.onboardSyndicate}
            </button>
          </div>
        )}
      </div>
      {starting && <StartOnboarding structure={starting} onCancel={() => setStarting(null)} />}
      <p className="muted small">{c.clientsHint}</p>
      {clients.length === 0 ? (
        <p className="muted">{c.noClients}</p>
      ) : (
        <table className="table table-stack clients-table">
          <thead>
            <tr>
              <th>{c.client}</th>
              <th>{c.kind}</th>
              <th>{c.size}</th>
              <th>{c.since}</th>
              <th aria-label={c.askAccess} />
            </tr>
          </thead>
          <tbody>
            {clients.map((x) => {
              const access = data.supportAccesses.find((a) => a.tenantId === x.id && (a.status === 'requested' || a.status === 'approved'));
              return (
                <Fragment key={x.id}>
                  <tr>
                    <td data-label={c.client}>
                      <strong>{x.name}</strong>
                      {x.status === 'onboarding' && <div><span className="badge onboarding-badge-ready">{c.onboardingStatus(x.onboardingDone, 6)}</span></div>}
                    </td>
                    <td data-label={c.kind}>{t.chooseKinds[x.legalStructure]}</td>
                    <td data-label={c.size}>{c.units(x.units)}</td>
                    <td data-label={c.since}>{date(x.createdAt.slice(0, 10))}</td>
                    <td className="clients-actions">
                      {x.status === 'onboarding' ? (
                        canOnboard && (
                          <Link to={`/s/${x.slug}/onboarding`} className="button button-small button-primary">
                            {c.continueOnboarding} →
                          </Link>
                        )
                      ) : access ? (
                        <span className="badge">{c.statuses[access.status as keyof typeof c.statuses] ?? access.status}</span>
                      ) : (
                        data.roles.includes('support') && (
                          <button type="button" className="button button-small" onClick={() => setAsking(asking === x.id ? null : x.id)}>
                            {c.askAccess}
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                  {asking === x.id && (
                    <tr className="console-ask-row">
                      <td colSpan={5}>
                        <AskAccess tenantId={x.id} onDone={() => setAsking(null)} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
      {data.supportAccesses.length > 0 && (
        <>
          <h3>{c.myRequests}</h3>
          <ul className="compact-list">
            {data.supportAccesses.map((a) => (
              <li key={a.id}>
                <span>
                  {a.tenantName} <span className="muted">· {a.reason}</span>
                </span>
                <span className="muted small">{date(a.requestedAt.slice(0, 10))}</span>
                <span className="badge">{c.statuses[a.status as keyof typeof c.statuses] ?? a.status}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function AskAccess({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const { t } = useI18n();
  const c = t.console;
  const api = useApi();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [hours, setHours] = useState('48');
  const ask = useMutation({
    mutationFn: () => api('/api/console/support-access', { method: 'POST', body: { tenantId, reason, hours: Number(hours) } }),
    onSuccess: () => (queryClient.invalidateQueries({ queryKey: ['console'] }), onDone()),
  });
  const submit = (e: FormEvent) => (e.preventDefault(), ask.mutate());
  return (
    <form className="form console-ask" onSubmit={submit}>
      <Field label={c.reason} hint={c.reasonHint}>
        <input value={reason} onChange={(e) => setReason(e.target.value)} required minLength={5} maxLength={500} />
      </Field>
      <Field label={c.hours}>
        <select value={hours} onChange={(e) => setHours(e.target.value)}>
          {['4', '24', '48', '72', '168'].map((h) => (
            <option key={h} value={h}>
              {c.hoursOption(Number(h))}
            </option>
          ))}
        </select>
      </Field>
      {ask.isError && <Alert>{errorMessage(ask.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={ask.isPending || reason.trim().length < 5}>
          {c.send}
        </button>
      </div>
    </form>
  );
}

/** Platform admins: who is on the Syndiqia team, with which roles; pick a person to edit their roles, or add one. */
function Team({ team }: { team: NonNullable<ConsoleData['team']> }) {
  const { t } = useI18n();
  const c = t.console;
  const people = [...new Map(team.map((m) => [m.userId, { userId: m.userId, email: m.email ?? '', name: m.name, pending: !m.lastSeenAt, roles: team.filter((x) => x.userId === m.userId).map((x) => x.role) }])).values()];
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [invited, setInvited] = useState<Invitation | null>(null);
  const person = people.find((p) => p.userId === editing);
  const done = (invitation?: Invitation) => {
    setEditing(null);
    setInvited(invitation ?? null);
  };
  const api = useApi();
  const resend = useMutation({
    mutationFn: (email: string) => api<{ emailFailed: boolean }>('/api/console/team/resend', { method: 'POST', body: { email } }),
    onSuccess: (res, email) => setInvited({ email, emailFailed: res.emailFailed }),
  });
  return (
    <section>
      <div className="section-header">
        <h2>{c.team}</h2>
        {editing !== 'new' && (
          <button type="button" className="button button-small" onClick={() => (setEditing('new'), setInvited(null), resend.reset())}>
            {c.addMember}
          </button>
        )}
      </div>
      <table className="table table-stack team-table">
        <thead>
          <tr>
            <th>{c.person}</th>
            <th>{c.rolesHeading}</th>
            <th aria-label={c.edit} />
          </tr>
        </thead>
        <tbody>
          {people.map((p) => (
            <tr key={p.userId} className={editing === p.userId ? 'selected' : undefined}>
              <td data-label={c.person}>
                <strong>{p.name ?? p.email}</strong>
                <div className="muted small">{p.email}</div>
                {p.pending && (
                  <div className="small">
                    <span className="badge badge-draft">{c.invitationPending}</span>{' '}
                    <button type="button" className="link-button small" disabled={resend.isPending} onClick={() => resend.mutate(p.email)}>
                      {c.resendInvitation}
                    </button>
                  </div>
                )}
              </td>
              <td data-label={c.rolesHeading}>
                <span className="console-roles">
                  {p.roles.map((r) => (
                    <span key={r} className={`badge badge-staff-${r}`}>
                      {c.roles[r]}
                    </span>
                  ))}
                </span>
              </td>
              <td className="team-actions">
                <button type="button" className="button button-small" onClick={() => setEditing(editing === p.userId ? null : p.userId)}>
                  {c.edit}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {invited && !invited.emailFailed && <Alert tone="info">{c.invitationSent(invited.email)}</Alert>}
      {invited?.emailFailed && (
        <Alert>
          {c.invitationNotSent(invited.email)}{' '}
          <button type="button" className="link-button" disabled={resend.isPending} onClick={() => resend.mutate(invited.email)}>
            {c.resendInvitation}
          </button>
        </Alert>
      )}
      {resend.isError && <Alert>{errorMessage(resend.error, t)}</Alert>}
      {editing === 'new' && <TeamEditor key="new" onDone={done} />}
      {person && <TeamEditor key={person.userId} person={person} onDone={done} />}
    </section>
  );
}

/**
 * Roles of one team member (or a new one, by sign-in email): checkboxes, saved as grants and removals. A new person
 * without an account receives an email to choose a password.
 */
type Invitation = { email: string; emailFailed: boolean };

function TeamEditor({ person, onDone }: { person?: { email: string; name: string | null; roles: StaffRole[] }; onDone: (invitation?: Invitation) => void }) {
  const { t } = useI18n();
  const c = t.console;
  const api = useApi();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState(person?.email ?? '');
  const [name, setName] = useState('');
  const [roles, setRoles] = useState<StaffRole[]>(person?.roles ?? []);
  const save = useMutation({
    mutationFn: async () => {
      const before = person?.roles ?? [];
      let invited = false;
      let emailFailed = false;
      for (const role of STAFF_ROLE_LIST) {
        const granted = roles.includes(role);
        if (granted === before.includes(role)) continue;
        const body = { email, role, granted, ...(!person && name.trim() ? { name: name.trim() } : {}) };
        const res = await api<{ ok: true; invited: boolean; emailFailed: boolean }>('/api/console/team', { method: 'POST', body });
        invited ||= res.invited;
        emailFailed ||= res.emailFailed;
      }
      return invited || emailFailed ? { email, emailFailed } : undefined;
    },
    onSuccess: async (invitation) => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: ['console'] }), queryClient.invalidateQueries({ queryKey: ['me'] })]);
      onDone(invitation);
    },
    onError: () => queryClient.invalidateQueries({ queryKey: ['console'] }),
  });
  const toggle = (role: StaffRole) => setRoles((r) => (r.includes(role) ? r.filter((x) => x !== role) : [...r, role]));
  const removingAll = !!person && roles.length === 0;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (removingAll && !window.confirm(c.confirmLeave(person!.name ?? person!.email))) return;
    save.mutate();
  };
  return (
    <form className="panel form team-editor" onSubmit={submit}>
      <h3>{person ? (person.name ?? person.email) : c.addMember}</h3>
      {person ? (
        <p className="muted small">{person.email}</p>
      ) : (
        <>
          <Field label={c.email} hint={c.emailHint}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </Field>
          <Field label={c.memberName} hint={c.memberNameHint}>
            <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
          </Field>
        </>
      )}
      <fieldset className="team-roles">
        <legend>{c.rolesHeading}</legend>
        {STAFF_ROLE_LIST.map((role) => (
          <label key={role} className="team-role">
            <input type="checkbox" checked={roles.includes(role)} onChange={() => toggle(role)} />
            <span>
              <strong>{c.roles[role]}</strong>
              <span className="muted small">{c.roleHelp[role]}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {removingAll && <Alert tone="info">{c.leaveWarning}</Alert>}
      {save.isError && <Alert>{errorMessage(save.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={save.isPending || !email || (!person && roles.length === 0)}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={() => onDone()}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}

const STAFF_ROLE_LIST: StaffRole[] = ['platform_admin', 'sales', 'support'];
